import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { paths } from '@/app/routes/paths';
import { authLogout, authSwitchOrganization } from '@/features/auth/api/auth-api';
import { messages } from '@/shared/i18n';
import { showErrorToast } from '@/shared/lib/toast';
import type { AccessibleOrganization } from '@/store';
import { mapSessionUser, useAuthStore } from '@/store';

import {
  buildGuardianProviderList,
  currentSessionOrganizationLabel,
  resolveChipDisplayName,
} from './helpers';
import type { TopBarVariant } from './types';

const tc = messages.common;
const guardianPortalCopy = messages.workspace.guardianDashboard;

interface UseAppTopBarAccountOptions {
  variant: TopBarVariant;
  userName: string;
}

/**
 * Account menu state, guardian org switching, and logout flow for {@link AppTopBar}.
 * Keeps the layout component mostly presentational.
 */
export function useAppTopBarAccount({ variant, userName }: UseAppTopBarAccountOptions) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const clearAuth = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();

  const userEmail = user?.email ?? '';
  const displayName = resolveChipDisplayName(userName, userEmail);

  const isGuardian = variant === 'guardian';
  const isGuardianDashboard = location.pathname === paths.guardianDashboard;
  const currentOrgId = user?.organization.id ?? '';

  const profileChipLabel =
    isGuardian && user ? currentSessionOrganizationLabel(user.organization) : displayName;

  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
  const menuWrapperRef = useRef<HTMLDivElement | null>(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const openLogoutConfirm = useCallback(() => {
    setMenuOpen(false);
    setConfirmOpen(true);
  }, []);

  const closeLogoutConfirm = useCallback(() => {
    setConfirmOpen((prev) => (isLoggingOut ? prev : false));
  }, [isLoggingOut]);

  const handleLogoutConfirmed = useCallback(async () => {
    if (isLoggingOut) {
      return;
    }
    setIsLoggingOut(true);
    let logoutError: unknown = null;
    try {
      await authLogout();
    } catch (e) {
      logoutError = e;
    }
    clearAuth();
    setConfirmOpen(false);
    setIsLoggingOut(false);
    if (logoutError) {
      showErrorToast(logoutError, {
        title: tc.logoutErrorTitle,
        body: tc.logoutError,
      });
    }
    void navigate(paths.login, { replace: true });
  }, [clearAuth, isLoggingOut, navigate]);

  const guardianProviderList = useMemo((): readonly AccessibleOrganization[] => {
    if (!isGuardian || !user) {
      return [];
    }
    return buildGuardianProviderList(user);
  }, [isGuardian, user]);

  const showGuardianProviderPicker = isGuardian && guardianProviderList.length > 0;

  const handleSelectOrganization = useCallback(
    async (organizationId: string) => {
      if (!user || organizationId === currentOrgId) {
        closeMenu();
        return;
      }
      setSwitchingOrgId(organizationId);
      try {
        const data = await authSwitchOrganization(organizationId);
        setUser(mapSessionUser(data.user));
        closeMenu();
        void navigate(paths.guardianDashboard, { replace: true });
      } catch (e) {
        showErrorToast(e, {
          title: guardianPortalCopy.switchOrganizationErrorTitle,
          body: guardianPortalCopy.switchOrganizationErrorBody,
        });
      } finally {
        setSwitchingOrgId(null);
      }
    },
    [closeMenu, currentOrgId, navigate, setUser, user],
  );

  return {
    menuWrapperRef,
    menuOpen,
    setMenuOpen,
    closeMenu,
    confirmOpen,
    isLoggingOut,
    closeLogoutConfirm,
    handleLogoutConfirmed,
    openLogoutConfirm,
    userEmail,
    displayName,
    profileChipLabel,
    isGuardian,
    isGuardianDashboard,
    currentOrgId,
    guardianProviderList,
    showGuardianProviderPicker,
    switchingOrgId,
    handleSelectOrganization,
  };
}
